import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./StudentCreate.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon.js";
import Card from "../../components/Card/index.js";
import TextInput from "../../components/TextInput/index.js";
import StudentSearchBarMultiple from "./StudentSearchBarMultiple/index.js";


// Services
import { getSessionGroupWithMembers, updateSessionGroup } from "../../services/sessionGroup.service.js";
import { showSuccess, showError } from "../../services/toast.service.js";

const SessionGroupsDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [sessionGroupData, setSessionGroupData] = useState({
    title: '',
    students_id: [],
  });
  const [originalData, setOriginalData] = useState(null);


  // Charger les données du groupe au montage du composant
  useEffect(() => {
    // Si on a des données depuis la navigation, les utiliser
    if (location.state?.sessionGroup) {
      const groupData = location.state.sessionGroup;
      
      const loadedData = {
        title: groupData.title || '',
        students_id: groupData.students_id || []
      };
      
      console.log('🏠 SessionGroupsDetail - Données chargées depuis navigation:', loadedData);
      console.log('🏠 SessionGroupsDetail - students_id récu:', loadedData.students_id);
      
      setSessionGroupData(loadedData);
      setOriginalData(loadedData);
      setLoadingData(false);
      return;
    }

    // Sinon, charger toutes les données
    const loadSessionGroup = async () => {
      if (!id) {
        showError('ID du groupe manquant');
        navigate('/admin/session-groups/');
        return;
      }

      try {
        setLoadingData(true);
        
        // Récupérer les données du groupe avec ses membres
        const groupData = await getSessionGroupWithMembers(id);
        
        const loadedData = {
          title: groupData.title || '',
          students_id: groupData.students_id || []
        };
        
        setSessionGroupData(loadedData);
        setOriginalData(loadedData);
        
      } catch (error) {
        console.error('Erreur lors du chargement du groupe:', error);
        showError('Impossible de charger les données du groupe');
        navigate('/admin/session-groups/');
      } finally {
        setLoadingData(false);
      }
    };

    loadSessionGroup();
  }, [id, navigate, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSessionGroupData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStudentsChange = (e) => {
    const { value } = e.target;
    setSessionGroupData((prev) => ({ ...prev, students_id: value }));
  };

  // Validation des champs requis
  const validateForm = () => {
    const errors = [];
    
    if (!sessionGroupData.title?.trim()) {
      errors.push('Le titre du groupe est requis');
    }
    
    if (!sessionGroupData.students_id || sessionGroupData.students_id.length === 0) {
      errors.push('Au moins un étudiant doit être sélectionné');
    }
    
    return errors;
  };

  // Vérifier si des modifications ont été apportées
  const hasChanges = () => {
    if (!originalData) return false;
    return (
      sessionGroupData.title !== originalData.title ||
      JSON.stringify(sessionGroupData.students_id.sort()) !== JSON.stringify(originalData.students_id.sort())
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation avant soumission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      const errorMessage = 'Veuillez corriger les erreurs suivantes:\n' + validationErrors.join('\n');
      showError(errorMessage);
      setError(errorMessage);
      setLoading(false);
      return;
    }

    // Vérifier s'il y a des changements
    if (!hasChanges()) {
      showError('Aucune modification détectée');
      setLoading(false);
      return;
    }

    try {
      // Pour la mise à jour, on n'envoie que le titre et les étudiants
      // Le partner_id reste inchangé côté serveur
      const dataToSend = {
        title: sessionGroupData.title,
        students_id: sessionGroupData.students_id
      };
      
      await updateSessionGroup(id, dataToSend);
      showSuccess("Groupe de session mis à jour avec succès!");
      
      // Mettre à jour les données originales
      setOriginalData({ ...sessionGroupData });
      
    } catch (err) {
      const errorMessage =
        "Impossible de mettre à jour le groupe de session: " +
        (err.message || "Veuillez vérifier les informations saisies.");
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si le formulaire est valide pour activer/désactiver le bouton
  const isFormValid = () => {
    return validateForm().length === 0 && hasChanges();
  };

  // Affichage de chargement
  if (loadingData) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <p>Chargement des données du groupe...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        <Link className={cn("button-white", styles.button)} to="/admin/session-groups/">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <div className={styles.formContainer}>
          {error && <div className={styles.error}>{error}</div>}
          
          <Card 
            className={styles.card}
            title="Modifier le groupe de session"
            classTitle={cn("title-blue", styles.title)}
          >
            <form onSubmit={handleSubmit}>
              <div className={styles.row}>
                <div className={styles.col}>
                  <TextInput
                    className={styles.field}
                    label="Titre du groupe"
                    name="title"
                    type="text"
                    placeholder="Entrez le titre du groupe"
                    value={sessionGroupData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className={styles.row}>
                <div className={styles.col}>
                  {!loadingData && (
                    <StudentSearchBarMultiple
                      className={styles.field}
                      label="Étudiants"
                      placeholder="Recherchez et sélectionnez les étudiants pour ce groupe"
                      value={sessionGroupData.students_id}
                      onSelectionChange={handleStudentsChange}
                      required
                      onSubmit={handleSubmit}
                      loading={loading}
                      isFormValid={isFormValid}
                      submitButtonText={hasChanges() ? "Mettre à jour le groupe" : "Aucune modification"}
                    />
                  )}
                </div>
              </div>
            
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SessionGroupsDetail;
